export type NodeZIndex = {
  id: string;
  zIndex: number;
};

/**
 * Assigns sequential zIndex values (0, 1, 2, ...) to nodes in their current array order.
 */
function reflow(ordered: NodeZIndex[]): NodeZIndex[] {
  return ordered.map((node, i) => ({ id: node.id, zIndex: i }));
}

/**
 * Returns true if the node order is identical between sorted input and reordered output.
 */
function hasSameOrder(original: string[], reordered: NodeZIndex[]): boolean {
  return original.every((id, i) => id === reordered[i].id);
}

/**
 * Move selected nodes forward (up) by one position in the z-order.
 * Each selected node swaps position with the unselected node above it.
 * Iterates top-to-bottom so adjacent selected nodes move as a group.
 * Returns all nodes with reflowed sequential zIndex values, or [] if no change.
 */
export function moveForward(allNodes: NodeZIndex[], selectedIds: Set<string>): NodeZIndex[] {
  if (allNodes.length === 0) {
    return [];
  }

  const sorted = [...allNodes].sort((a, b) => a.zIndex - b.zIndex);
  const originalOrder = sorted.map((n) => n.id);

  for (let i = sorted.length - 1; i >= 0; i--) {
    if (!selectedIds.has(sorted[i].id)) {
      continue;
    }

    if (i + 1 < sorted.length && !selectedIds.has(sorted[i + 1].id)) {
      [sorted[i], sorted[i + 1]] = [sorted[i + 1], sorted[i]];
    }
  }

  if (hasSameOrder(originalOrder, sorted)) {
    return [];
  }

  return reflow(sorted);
}

/**
 * Move selected nodes back (down) by one position in the z-order.
 * Each selected node swaps position with the unselected node below it.
 * Iterates bottom-to-top so adjacent selected nodes move as a group.
 * Returns all nodes with reflowed sequential zIndex values, or [] if no change.
 */
export function moveBack(allNodes: NodeZIndex[], selectedIds: Set<string>): NodeZIndex[] {
  if (allNodes.length === 0) {
    return [];
  }

  const sorted = [...allNodes].sort((a, b) => a.zIndex - b.zIndex);
  const originalOrder = sorted.map((n) => n.id);

  for (let i = 0; i < sorted.length; i++) {
    if (!selectedIds.has(sorted[i].id)) {
      continue;
    }

    if (i - 1 >= 0 && !selectedIds.has(sorted[i - 1].id)) {
      [sorted[i - 1], sorted[i]] = [sorted[i], sorted[i - 1]];
    }
  }

  if (hasSameOrder(originalOrder, sorted)) {
    return [];
  }

  return reflow(sorted);
}

/**
 * Send selected nodes to the front (top of z-order).
 * Places selected nodes above all unselected nodes, preserving relative order.
 * Returns all nodes with reflowed sequential zIndex values, or [] if no change.
 */
export function sendToFront(allNodes: NodeZIndex[], selectedIds: Set<string>): NodeZIndex[] {
  if (allNodes.length === 0) {
    return [];
  }

  const sorted = [...allNodes].sort((a, b) => a.zIndex - b.zIndex);
  const originalOrder = sorted.map((n) => n.id);
  const unselected = sorted.filter((n) => !selectedIds.has(n.id));
  const selected = sorted.filter((n) => selectedIds.has(n.id));

  if (unselected.length === 0 || selected.length === 0) {
    return [];
  }

  const reordered = [...unselected, ...selected];
  if (hasSameOrder(originalOrder, reordered)) {
    return [];
  }

  return reflow(reordered);
}

/**
 * Send selected nodes to the back (bottom of z-order).
 * Places selected nodes below all unselected nodes, preserving relative order.
 * Returns all nodes with reflowed sequential zIndex values, or [] if no change.
 */
export function sendToBack(allNodes: NodeZIndex[], selectedIds: Set<string>): NodeZIndex[] {
  if (allNodes.length === 0) {
    return [];
  }

  const sorted = [...allNodes].sort((a, b) => a.zIndex - b.zIndex);
  const originalOrder = sorted.map((n) => n.id);
  const unselected = sorted.filter((n) => !selectedIds.has(n.id));
  const selected = sorted.filter((n) => selectedIds.has(n.id));

  if (unselected.length === 0 || selected.length === 0) {
    return [];
  }

  const reordered = [...selected, ...unselected];
  if (hasSameOrder(originalOrder, reordered)) {
    return [];
  }

  return reflow(reordered);
}
